import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';

// Import all locale data
import 'dayjs/locale/en';
import 'dayjs/locale/hi';
import 'dayjs/locale/ta';
import 'dayjs/locale/id';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/ms';
import 'dayjs/locale/th';
import 'dayjs/locale/vi';
import 'dayjs/locale/de';

// Enable plugins
dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);
dayjs.extend(calendar);

/**
 * Map i18n language codes to dayjs locale codes
 */
const LOCALE_MAP: Record<string, string> = {
  en: 'en',
  hi: 'hi',
  ta: 'ta',
  tl: 'en', // Tagalog not available, fallback to English
  id: 'id',
  zh: 'zh-cn',
  ms: 'ms',
  th: 'th',
  vi: 'vi',
  my: 'en', // Burmese not available, fallback to English
  de: 'de',
};

/**
 * Set dayjs locale based on i18n language code
 */
export function setDayjsLocale(languageCode: string): void {
  const dayjsLocale = LOCALE_MAP[languageCode] || 'en';
  dayjs.locale(dayjsLocale);
}

/**
 * Format a date with the current locale
 */
export function formatDate(date: string | Date | dayjs.Dayjs, format: string = 'LL'): string {
  return dayjs(date).format(format);
}

/**
 * Get relative time (e.g., "2 hours ago", "in 3 days")
 */
export function getRelativeTime(date: string | Date | dayjs.Dayjs): string {
  return dayjs(date).fromNow();
}

/**
 * Format date with calendar format (e.g., "Today at 2:30 PM", "Yesterday at 10:00 AM")
 */
export function getCalendarTime(date: string | Date | dayjs.Dayjs): string {
  return dayjs(date).calendar(null, {
    sameDay: '[Today at] LT',
    nextDay: '[Tomorrow at] LT',
    nextWeek: 'dddd [at] LT',
    lastDay: '[Yesterday at] LT',
    lastWeek: '[Last] dddd [at] LT',
    sameElse: 'L',
  });
}

/**
 * Common date format patterns
 */
export const DateFormats = {
  LONG_DATE: 'LL', // September 4, 1986
  SHORT_DATE: 'L', // 09/04/1986
  LONG_DATE_TIME: 'LLL', // September 4, 1986 8:30 PM
  FULL_DATE_TIME: 'LLLL', // Thursday, September 4, 1986 8:30 PM
  TIME: 'LT', // 8:30 PM
  TIME_WITH_SECONDS: 'LTS', // 8:30:00 PM
  MONTH_DAY: 'MMM D', // Sep 4
  YEAR_MONTH: 'YYYY MMMM', // 1986 September
} as const;

export default dayjs;
